import { useState } from "react";
import { Plus, Trash2, Copy, ExternalLink, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../../../lib/adminApi";
import { money } from "../../../lib/api";
import { Card, KV, TextInput, TextArea, SelectInput, CheckInput, Btn, run, emailToast, fmtDateTime } from "../AdminUI";

const METHODS = [{ value: "card_link", label: "Card (payment link)" }, { value: "bank_transfer", label: "Bank transfer / wire" }, { value: "cod", label: "Cash on delivery" }];

const Editor = ({ sh, initial, apply, done }) => {
  const [f, setF] = useState(initial);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const setLine = (i, k) => (v) => setF((p) => ({ ...p, line_items: p.line_items.map((li, j) => (j === i ? { ...li, [k]: v } : li)) }));
  const total = f.line_items.reduce((s, li) => s + (Number(li.amount) || 0), 0);
  const save = async () => {
    const body = { ...f, line_items: f.line_items.map((li) => ({ label: li.label, amount: Number(li.amount) || 0 })), payment_link: f.payment_link || null };
    const r = await run(() => adminApi.post(`/admin/shipments/${sh.tracking_number}/invoice`, body), "Invoice saved");
    if (r) { apply(r.data); done(); }
  };
  return (
    <Card title={sh.invoice ? `Edit invoice ${sh.invoice.number}` : "New invoice"} testid="invoice-editor">
      <div className="space-y-2" data-testid="invoice-line-items">
        {f.line_items.map((li, i) => (
          <div key={i} className="grid grid-cols-[1fr_140px_40px] gap-2 items-end">
            <TextInput label={i === 0 ? "Line item" : ""} value={li.label} onChange={setLine(i, "label")} testid={`invoice-line-label-${i}`} />
            <TextInput label={i === 0 ? "Amount (USD)" : ""} type="number" step="0.01" value={li.amount} onChange={setLine(i, "amount")} testid={`invoice-line-amount-${i}`} />
            <button type="button" onClick={() => setF((p) => ({ ...p, line_items: p.line_items.filter((_, j) => j !== i) }))} className="h-10 grid place-items-center border border-slate-300 text-slate-500 hover:text-red-600" data-testid={`invoice-line-remove-${i}`} aria-label="Remove line"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
      <div className="mt-3 flex items-center justify-between">
        <Btn variant="ghost" onClick={() => setF((p) => ({ ...p, line_items: [...p.line_items, { label: "", amount: 0 }] }))} data-testid="invoice-add-line"><Plus className="w-4 h-4" />Add line</Btn>
        <div className="font-display font-black text-2xl text-slate-900" data-testid="invoice-editor-total">Total {money(total)}</div>
      </div>
      <div className="mt-6 grid sm:grid-cols-2 gap-4">
        <TextInput label="Due date" type="date" value={f.due_date} onChange={set("due_date")} testid="invoice-due-date" />
        <TextInput label="Card payment link (https://…)" value={f.payment_link} onChange={set("payment_link")} testid="invoice-payment-link" placeholder="Pasted by finance" />
        <TextArea label="Payment instructions" rows={4} value={f.payment_instructions} onChange={set("payment_instructions")} testid="invoice-instructions" className="sm:col-span-2" />
        <TextArea label="Invoice notes (shown on invoice)" value={f.notes} onChange={set("notes")} testid="invoice-notes" className="sm:col-span-2" />
        <CheckInput label="Offer cash on delivery on this invoice" checked={f.cod_available} onChange={set("cod_available")} testid="invoice-cod-available" />
      </div>
      <div className="mt-6 flex justify-end gap-3">
        <Btn variant="ghost" onClick={done} data-testid="invoice-cancel">Cancel</Btn>
        <Btn onClick={save} data-testid="invoice-save">Save invoice</Btn>
      </div>
    </Card>
  );
};

const InvoiceSummary = ({ sh, apply, edit }) => {
  const inv = sh.invoice;
  const url = `${window.location.origin}/invoice/${inv.token}`;
  const send = async () => {
    const r = await run(() => adminApi.post(`/admin/shipments/${sh.tracking_number}/invoice/send`));
    if (r) { apply(r.data); emailToast(r.data.email); }
  };
  return (
    <Card title={`Invoice ${inv.number}`} testid="invoice-summary" actions={<>
      <Btn variant="ghost" onClick={edit} data-testid="invoice-edit-button">Edit</Btn>
      <Btn onClick={send} disabled={["Cancelled", "Delivered"].includes(sh.status)} data-testid="invoice-send-button"><Send className="w-4 h-4" />{inv.sent_at ? "Resend invoice" : "Send invoice"}</Btn>
    </>}>
      <dl>
        <KV k="Status" v={inv.status.toUpperCase()} testid="invoice-status" />
        <KV k="Total" v={money(inv.total)} testid="invoice-total" />
        <KV k="Due date" v={inv.due_date} />
        <KV k="Sent" v={inv.sent_at ? fmtDateTime(inv.sent_at) : "Not sent yet"} />
        <KV k="Card payment link" v={inv.payment_link} />
        <KV k="Cash on delivery offered" v={inv.cod_available ? "Yes" : "No"} />
      </dl>
      <div className="mt-4 flex flex-wrap items-center gap-2 bg-slate-50 border border-slate-200 p-3">
        <span className="font-mono text-xs text-slate-700 break-all flex-1" data-testid="invoice-public-url">{url}</span>
        <Btn variant="ghost" onClick={() => { navigator.clipboard?.writeText(url); toast.success("Link copied"); }} data-testid="invoice-copy-link"><Copy className="w-4 h-4" /></Btn>
        <a href={url} target="_blank" rel="noreferrer" className="btn-ghost h-10 px-3" data-testid="invoice-open-link"><ExternalLink className="w-4 h-4" /></a>
      </div>
    </Card>
  );
};

const PaymentPanel = ({ sh, apply }) => {
  const [method, setMethod] = useState("bank_transfer");
  const [reference, setReference] = useState("");
  const [notify, setNotify] = useState(true);
  const confirm = async () => {
    const r = await run(() => adminApi.post(`/admin/shipments/${sh.tracking_number}/payment`, { method, reference, notify }), "Payment confirmed");
    if (r) { apply(r.data); emailToast(r.data.email); }
  };
  const toggleCod = async (v) => {
    const r = await run(() => adminApi.patch(`/admin/shipments/${sh.tracking_number}`, { cod_agreed: v }), v ? "COD agreed: carrier booking unlocked" : "COD agreement removed");
    if (r) apply(r.data);
  };
  const p = sh.payment;
  return (
    <Card title="Payment" testid="payment-panel">
      {p ? (
        <div className="flex items-start gap-3" data-testid="payment-confirmed">
          <CheckCircle2 className="w-6 h-6 text-emerald-600 shrink-0" />
          <dl className="flex-1">
            <KV k="Method" v={METHODS.find((m) => m.value === p.method)?.label} testid="payment-method" />
            <KV k="Reference" v={p.reference} />
            <KV k="Confirmed" v={`${fmtDateTime(p.confirmed_at)} by ${p.confirmed_by || "admin"}`} />
          </dl>
        </div>
      ) : (
        <div className="grid sm:grid-cols-3 gap-4 items-end">
          <SelectInput label="Method" value={method} onChange={setMethod} options={METHODS} testid="payment-method-select" />
          <TextInput label="Reference (optional)" value={reference} onChange={setReference} testid="payment-reference" />
          <Btn onClick={confirm} disabled={sh.status === "Cancelled"} data-testid="payment-confirm-button">Mark payment confirmed</Btn>
          <CheckInput label="Notify customer by email" checked={notify} onChange={setNotify} testid="payment-notify" className="sm:col-span-3" />
        </div>
      )}
      <div className="mt-5 pt-5 border-t border-slate-200">
        <CheckInput label="Cash on delivery agreed (allows Carrier Booked before payment clears)" checked={sh.cod_agreed} onChange={toggleCod} testid="cod-agreed-checkbox" />
        {!sh.payment_gate_ok && <p className="mt-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 px-3 py-2" data-testid="payment-gate-warning">Transport is booked once payment clears. Carrier Booked and later stages are blocked until payment is confirmed or COD is agreed.</p>}
      </div>
    </Card>
  );
};

export const InvoiceTab = ({ sh, apply }) => {
  const [draft, setDraft] = useState(null);
  const startEdit = async () => {
    const r = await run(() => adminApi.get(`/admin/shipments/${sh.tracking_number}/invoice/suggest`));
    if (r) setDraft(r.data);
  };
  return (
    <div className="space-y-6" data-testid="invoice-tab">
      {draft ? <Editor sh={sh} initial={draft} apply={apply} done={() => setDraft(null)} />
        : sh.invoice ? <InvoiceSummary sh={sh} apply={apply} edit={startEdit} />
          : (
            <Card title="Invoice" testid="invoice-empty">
              <p className="text-sm text-slate-600 mb-4">No invoice yet. Generate one from the estimate and adjust the line items before sending.</p>
              <Btn onClick={startEdit} disabled={sh.status === "Cancelled"} data-testid="invoice-generate-button">Generate invoice</Btn>
            </Card>
          )}
      <PaymentPanel sh={sh} apply={apply} />
    </div>
  );
};
